import { Component, Inject, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { RouterModule, RouterOutlet } from '@angular/router';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { AuthenticationResult, EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { filter, Subject, switchMap, takeUntil, throwError } from 'rxjs';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { TOKEN_KEY } from '@shared/config/config-token';
import { AuthMsalService } from '@shared/services/auth-msal.service';
import { UserProfileComponent } from '@component/user/user-profile/user-profile.component';
import { UserRole } from '@shared/enums/user-role-enum';
import { RoleService } from '@shared/services/role.service';
import { environment } from '@environment/environment';
import { UserService } from '@component/user/services/user.service';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzBackTopModule } from 'ng-zorro-antd/back-top';
import { NzImageModule } from 'ng-zorro-antd/image';
import { DerichebourgColorEnum } from '@shared/enums/color-derichebourg-enum';
import { QrCodeAppComponent } from "./components/home/qr-code-app/qr-code-app.component";
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { UserProfileDto } from '@model/user/user-profile-dto';
import { NzI18nService, NzI18nInterface, en_US, de_DE, es_ES, zh_CN } from 'ng-zorro-antd/i18n';
import { customFrFR } from '@config/custom-config-FR-fr';
import moment from 'moment';
import { setNoDataText } from '@component/kpi/charts/chart-plugins/chart-no-data-plugin';
import { normalizeLanguage } from '@shared/util/language.util';

@Component({
  selector: 'app-root',
  imports: [
    CommonModule,
    RouterOutlet,
    NzIconModule,
    NzLayoutModule,
    NzMenuModule,
    RouterModule,
    NzDividerModule,
    NzButtonModule,
    UserProfileComponent,
    NzTooltipModule,
    NzBackTopModule,
    NzImageModule,
    QrCodeAppComponent,
    TranslateModule
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  /**
   * Is sidebar collapsed
   *
   * @public
   * @type {boolean}
   */
  public isCollapsed: boolean = false;
  /**
   * Subject to handle component destruction
   *
   * @private
   * @readonly
   * @type {*}
   */
  private readonly _destroying$ = new Subject<void>();
  /**
   * User is admin perimeter or higher
   *
   * @public
   * @type {boolean}
   */
  public adminPerimeterAccess: boolean = false;
  /**
   * Super admin state
   *
   * @public
   * @type {boolean}
 */
  public isSuperAdmin: boolean = false;
  /**
 * Admin state
 *
 * @public
 * @type {boolean}
*/
  public isAdmin: boolean = false;
  /**
   * Admin management state
   *
   * @public
   * @type {boolean}
   */
  public isAdminManagement: boolean = false;
  /**
   * Uat env state
   *
   * @public
   * @type {boolean}
   */
  public uatEnv: boolean = environment.isUatEnv;
  /**
   * Dev env state
   *
   * @public
   * @type {boolean}
   */
  public devEnv: boolean = environment.isDevEnv;
  /**
 * Local env state
 *
 * @public
 * @type {boolean}
 */
  public localEnv: boolean = environment.isLocalEnv;
  /**
   * Color tooltip
   *
   * @public
   * @type {string}
   */
  public derichebourgColorBlue: string = DerichebourgColorEnum.BLUE;
  /**
   * Is user loaded
   *
   * @public
   * @type {boolean}
   */
  public isUserLoaded: boolean = false;

  private translate = inject(TranslateService);

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private broadcastService: MsalBroadcastService,
    private msalService: MsalService,
    private authMsalService: AuthMsalService,
    private readonly _roleService: RoleService,
    private readonly _userService: UserService,
    private readonly _nzI18nService: NzI18nService
  ) {

  }

  ngOnInit() {
    const initialLang = normalizeLanguage(this.translate.getCurrentLang() || this.translate.getBrowserLang() || 'fr');
    this.translate.use(initialLang);
    this.applyNzLocale(initialLang);

    this.translate.onLangChange
      .pipe(takeUntil(this._destroying$))
      .subscribe(({ lang }) => {
        this.applyNzLocale(lang);
      });

    this.authMsalService.getCurrentUser()
      .pipe(
        filter((user): user is UserProfileDto => !!user),
        takeUntil(this._destroying$)
      )
      .subscribe((user) => {
        const apiLanguage = normalizeLanguage(user.langageUser?.label);

        if (apiLanguage && this.translate.currentLang !== apiLanguage) {
          this.translate.use(apiLanguage);
        }
      });

    this.broadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        if (payload.account) {
          this.msalService.instance.setActiveAccount(payload.account);
          if (payload.account.idToken) {
            localStorage.setItem(TOKEN_KEY, payload.account.idToken)
          }
          // this.checkAndSetActiveAccount()
        }
      });


    this.broadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount()
      })

  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  checkAndSetActiveAccount() {
    const activeAccount = this.msalService.instance.getActiveAccount();
    if (!activeAccount && this.msalService.instance.getAllAccounts().length > 0) {
      const accounts = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(accounts[0]);
    }

    this.authMsalService.fetchAuthenticatedUser()
      .pipe(
        switchMap((user) => {
          this.isUserLoaded = true;
          if (user) {
            this.adminPerimeterAccess = false;
            this.isSuperAdmin = false;
            this.isAdmin = false;
            this.isAdminManagement = false;

            // if no id, its a fake account
            if (user.guid == '') {
              return throwError(() => new Error('Not enough right'));
            }

            // Update last login attribut every time user return on the app
            const updateLoginRequest$ = this._userService.updateLastLogin();
            const userIsActive = user.isActive !== false;

            if (user.role && userIsActive) {
              // Set global user adminperimeter access
              if (this._roleService.hasHigherRole(user.role.name, UserRole.ADMIN_PERIMETER)) {
                this.adminPerimeterAccess = true;
              }

              // Set global user super admin access
              if (this._roleService.hasHigherRole(user.role.name, UserRole.SUPER_ADMIN)) {
                this.isSuperAdmin = true;
              }

              if (this._roleService.hasHigherRole(user.role.name, UserRole.ADMIN)) {
                this.isAdmin = true;
              }

              if (this._roleService.hasHigherRole(user.role.name, UserRole.ADMIN_MANAGEMENT)) {
                this.isAdminManagement = true;
              }
            }

            return updateLoginRequest$
          } else {
            return throwError(() => new Error('Utilisateur non récupéré'));
          }
        }),
        takeUntil(this._destroying$)
      ).subscribe({
        next: () => {

        },
        error: () => console.error('User non récupéré')
      });
  }

  private applyNzLocale(lang: string): void {
    const normalizedLang = (lang || 'fr').toLowerCase();
    const localeMap: Record<string, NzI18nInterface> = {
      fr: customFrFR,
      en: en_US,
      de: de_DE,
      es: es_ES,
      zh: zh_CN
    };

    const nzLocale = localeMap[normalizedLang] ?? customFrFR;
    this._nzI18nService.setLocale(nzLocale);
    // ng-zorro locale ids ('fr', 'en', 'de', 'es', 'zh-cn') match moment locale names
    moment.locale(nzLocale.locale);

    // Sync the text drawn by the global Chart.js no-data plugin
    this.translate.get('common.no_data')
      .pipe(takeUntil(this._destroying$))
      .subscribe((text: string) => setNoDataText(text));
  }
}
